import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMonths, format, isAfter, lastDayOfMonth } from 'date-fns';
import TableComponent from '../../commons/components/TableComponent';
import {
  getIndividualDetails,
  resetIndividualDetailsState,
  saveCurrentDateRangeForIndividual,
} from '../store/actions';
import { getDateFromString, getModifiedStartEndDate, isPartialMatch } from '../../../utils/helpers';
import { Placeholder } from '../../commons/components';
import { filterOptions, filterType } from '../../../constants/filterOptions';

const getDivisorValue = (field) => {
  if (field === 'total_steps') {
    return 1000000;
  }
  return null;
};

const IndividualDetailsLast12MonthsContainer = ({
  field,
  companySelected,
  startValue,
  endValue,
  selectedFilteredType,
  onClearKpiState,
}) => {
  const corporateList = useSelector((state) => state.kpi.corporateList);
  const corpIdNameMap = useSelector((state) => state.kpi.cropIdNameMap);
  const freemiumCropIdNameMap = useSelector((state) => state.kpi.freemiumCropIdNameMap);
  const individualDetails = useSelector((state) => state.kpi.individualDetails);
  const rows = individualDetails.data && individualDetails.data[field];
  const dateRange = individualDetails.currentDateRange?.[field];
  const payloadRef = useRef();
  const hardRefresh = useRef(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (startValue && endValue) {
      dispatch(
        saveCurrentDateRangeForIndividual({
          [field]: {
            ...(dateRange || {}),
            quarterly: false,
            startDate: format(new Date(startValue), 'yyyy-MM-dd'),
            endDate: format(new Date(endValue), 'yyyy-MM-dd'),
            selectedFilter: filterOptions.find(
              (item) => item.value === selectedFilteredType || filterType.custom
            ),
          },
        })
      );
    }
  }, [startValue, endValue, selectedFilteredType]);

  function getPayload() {
    let tmpStartDate = format(new Date(startValue), 'yyyy-MM-dd');
    let tmpEndDate = format(new Date(endValue), 'yyyy-MM-dd');

    if (!hardRefresh.current) {
      const { startDate: updatedStartDate, endDate: updatedEndDate } = getModifiedStartEndDate(
        dateRange,
        tmpStartDate,
        tmpEndDate
      );
      tmpStartDate = updatedStartDate;
      tmpEndDate = updatedEndDate;
    }
    return {
      companyIdList: companySelected.map((c) => c._id),
      startDate: tmpStartDate,
      endDate: tmpEndDate,
      viewedFrom: 'kpi',
      quarterly: false,
      field: field,
      selectedFilterType: selectedFilteredType,
      hardRefresh: hardRefresh.current,
    };
  }

  useEffect(() => {
    if (
      !Array.isArray(companySelected) ||
      companySelected.length !== 1 ||
      !startValue ||
      !endValue
    ) {
      return;
    }

    if (
      rows &&
      rows.length > 0 &&
      !hardRefresh.current &&
      format(new Date(startValue), 'yyyy-MM-dd') === dateRange.oldStartDate &&
      format(new Date(endValue), 'yyyy-MM-dd') === dateRange.oldEndDate
    ) {
      /** data exist. no need to fetch */
      return;
    }
    fetchData();
    dispatch(
      saveCurrentDateRangeForIndividual({
        [field]: {
          ...dateRange,
          quarterly: false,
          oldStartDate: format(new Date(startValue), 'yyyy-MM-dd'),
          oldEndDate: format(new Date(endValue), 'yyyy-MM-dd'),
        },
      })
    );
  }, [companySelected, startValue, endValue, corporateList]);

  function getMonthsInRange(startDate, endDate) {
    const months = [];
    let currentDate = new Date(startDate);
    let currentEndDate = new Date(endDate);
    while (
      isAfter(currentEndDate, currentDate) ||
      currentDate.getMonth() === currentEndDate.getMonth()
    ) {
      months.push(format(lastDayOfMonth(currentDate), 'yyyy-MM-dd'));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  const monthsInRange = getMonthsInRange(startValue, endValue);
  const divisor = getDivisorValue(field);

  const getHeaderSubtitle = (month) => {
    const sDate = getDateFromString(startValue);
    const eDate = getDateFromString(endValue);

    if (startValue && isPartialMatch(month, startValue) && sDate.getDate() > 1) {
      return `${format(sDate, 'do')} - ${format(lastDayOfMonth(sDate), 'do MMM yyyy')}`;
    } else if (
      endValue &&
      isPartialMatch(month, endValue) &&
      eDate.getDate() < 31 &&
      eDate.getMonth() !== sDate.getMonth()
    ) {
      return `${format(eDate, 'do MMM yyyy')}`;
    }
    return;
  };

  const showLoaderForMonth = (month) => {
    const payload = payloadRef.current;
    if (
      (payload &&
        new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
      (payload && isPartialMatch(month, payload.endDate))
    ) {
      return true;
    }
    return false;
  };

  const getSubscriptionType = (corporateId) => {
    const corporate = corporateList.data.find((corp) => corp._id === corporateId);
    if (corporate) {
      return `(${corporate.subscription_type})`
    }
    return '(free)';
  };

  function findMinMaxDates(dateArray) {
    if (dateArray.length === 0) {
      return { minDate: null, maxDate: null };
    }
  
    const dateObjects = dateArray.map(dateString => new Date(dateString));
    const minDate = new Date(Math.min.apply(null, dateObjects));
    const maxDate = new Date(Math.max.apply(null, dateObjects));
  
    const formattedMinDate = `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}-${String(minDate.getDate()).padStart(2, '0')}`;
    const formattedMaxDate = `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}-${String(maxDate.getDate()).padStart(2, '0')}`;
  
    return { minDate: formattedMinDate, maxDate: formattedMaxDate };
  }

  // Define columns for the table
  const columns = useMemo(
    () => [
      {
        Header: 'Enterprise Name',
        accessor: 'corporate_name',
      },
      ...monthsInRange.map((month, index) => ({
        id: `month-${index}`,
        Header: (
          <div className='d-flex flex-column'>
            <span>{format(new Date(month), 'MMMM')}</span>
            {getHeaderSubtitle() && (
              <span className='subtitle'>
                {'('}
                {getHeaderSubtitle()}
                {')'}
              </span>
            )}
          </div>
        ),
        accessor: (item) => {
          return item[month] || 0;
        },
        Cell: ({ cell }) =>
          individualDetails.loading && showLoaderForMonth(month) ? (
            <Placeholder width='90%' height={20} />
          ) : divisor && cell.value ? (
            (Number(cell.value) / Number(divisor)).toFixed(2)
          ) : cell.value ? (
            cell.value?.toLocaleString('en-US')
          ) : (
            '-'
          ),
      })),
    ],
    [field, individualDetails.loading, monthsInRange]
  );

  const data = useMemo(() => {
    if (!rows || rows.length === 0 || !startValue || !endValue) {
      const placeholderRows = Array.from({ length: 20 }, (_, index) => ({
        corporate_name: <Placeholder width='90%' height={20} />,
        ...monthsInRange.reduce((acc, month) => {
          acc[month] = '-';
          return acc;
        }, {}),
      }));
      return placeholderRows;
    }

    let corporateWithActivity = rows;
    let activeCorporateIds = corporateWithActivity.map((corporate) => corporate.corporate_id);
    let corporateWithoutActivity = corporateList.data.filter(
      (corporate) => !activeCorporateIds.includes(corporate._id)
    );

    let copyRows = [];
    let i = 0;

    let startDate = format(new Date(startValue), 'yyyy-MM-dd');
    let endDate = format(new Date(endValue), 'yyyy-MM-dd');

    corporateWithActivity.map((item) => {
      const dates = item.data.map((d) => d.active_date);
      const { minDate, maxDate } = findMinMaxDates(dates);
      if (minDate && maxDate && new Date(startDate) <= new Date(maxDate) && new Date(endDate) >= new Date(minDate)) {
        i++;
        copyRows.push({
          corporate_id: item.corporate_id,
          corporate_name: `${corpIdNameMap[item.corporate_id] || freemiumCropIdNameMap[item.corporate_id]}`,
          ...monthsInRange.reduce((acc, month) => {
            const dataForMonth = item.data.find((d) => isPartialMatch(d.active_date, month));
            acc[month] = dataForMonth ? dataForMonth[field]?.toLocaleString('en-US') : '';
            return acc;
          }, {})
        });
      }
    });

    // corporateWithoutActivity.forEach((corporate) => {
    //   copyRows.push({
    //     corporate_id: corporate._id,
    //     corporate_name:
    //       corporate.entity_name ||
    //       corpIdNameMap[corporate._id] ||
    //       freemiumCropIdNameMap[corporate._id],
    //     ...monthsInRange.reduce((acc, month) => {
    //       acc[month] = 0;
    //       return acc;
    //     }, {}),
    //   });
    // });
    return copyRows.filter(
      (item) => companySelected[0]?._id === 'ALL' || item.corporate_id === companySelected[0]?._id
    );
  }, [field, rows, companySelected, monthsInRange, corporateList, corpIdNameMap, freemiumCropIdNameMap]);

  function fetchData() {
    const payload = getPayload();
    payloadRef.current = payload;
    dispatch(getIndividualDetails(payload));
    hardRefresh.current = false;
  }

  return (
    <>
      <button
        onClick={() => {
          hardRefresh.current = true;
          onClearKpiState();
        }}
        className='btn btn-primary hard-reload'
      >
        Refresh
      </button>
      <TableComponent data={data} columns={columns} />
    </>
  );
};

export default IndividualDetailsLast12MonthsContainer;
